import { initializeApp } from 'firebase/app';
import {
  getAuth,
  initializeAuth,
  browserLocalPersistence,
  GoogleAuthProvider,
  OAuthProvider,
} from 'firebase/auth';
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager, memoryLocalCache } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { enforceCacheBudget } from './utils/lsCache';

// ── localStorage-Headroom für Firestore sichern (Boot-Self-Heal) ──────────────
// Firestores persistentLocalCache schreibt seinen Multi-Tab-Sync-State in
// localStorage ("firestore_targets_…"). Liefen die regenerierbaren Preis-Caches
// (red_pxhist_/redapp_cg_hist_*) voll, scheiterte dieser setItem mit
// QuotaExceededError → "INTERNAL UNHANDLED ERROR" → kompletter App-Crash.
// Wir erzwingen das Cache-Budget HIER, VOR initializeFirestore(), damit bereits
// vollgelaufene Installationen sich beim Laden selbst heilen (kein manuelles
// Cache-Leeren nötig). Best effort — wirft nie.
enforceCacheBudget();

// Auf native iOS den iOS-Key aus GoogleService-Info.plist verwenden.
// Der Browser-Key aus env (REACT_APP_FIREBASE_API_KEY) wird von Firebase für
// Token-Refresh (securetoken.googleapis.com) genutzt — ist er falsch, scheitert
// der Refresh und Firebase meldet den User ab. iOS-Key ist garantiert korrekt.
const _isNative = typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();
const _iosApiKey = 'AIzaSyAOPOlqFDVUjfp1AfodzHbLnxVdx7EJC2I'; // GoogleService-Info.plist

// Native iOS-Networking: capacitor.config.json aktiviert das CapacitorHttp-Plugin
// ("CapacitorHttp": { "enabled": true }). Dadurch werden window.fetch UND
// XMLHttpRequest im WKWebView automatisch durch native URLSession ersetzt — für
// ALLE Hosts. Das behebt die sporadischen status-0/AbortError-Fehler des WKWebView-
// Fetch-Layers über die gesamte App: Auth-Token-Refresh (securetoken), Firestore-
// LongPolling-Channel und die eigenen API-/CoinGecko-Calls. Ein manueller
// fetch-Monkey-Patch ist damit überflüssig (war fragil: synthetische Response).

const firebaseConfig = {
  apiKey: _isNative ? _iosApiKey : process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

export const app = initializeApp(firebaseConfig);

// ── App Check (reCAPTCHA Enterprise) ──────────────────────────────────────────
// VORÜBERGEHEND DEAKTIVIERT (APP_CHECK_ENABLED = false).
//
// Grund: `grecaptcha.enterprise.execute()` für unseren Site-Key hängt auf
// app.redcrypto.de dauerhaft (kommt nie zurück, kein Fehler). Dadurch hängt
// initializeAppCheck → getToken, und weil Firebase Auth mit
// isTokenAutoRefreshEnabled jedes Auth-Request auf ein App-Check-Token wartet,
// dreht JEDER Login (Google + E-Mail) endlos, ohne dass je eine Netzwerk-
// Anfrage rausgeht. Live verifiziert: execute() Timeout 18s, getToken Timeout
// 45s, identitytoolkit direkt aber problemlos erreichbar (HTTP 400 in <500ms).
//
// App Check läuft serverseitig nur im MONITOR-Modus (nicht erzwungen — Requests
// ohne Token werden mit 400/normal beantwortet, nicht 403), daher ist das
// Deaktivieren gefahrlos. WIEDER AKTIVIEREN, sobald der reCAPTCHA-Enterprise-Key
// repariert ist: in der Google-Cloud-Console „reCAPTCHA Enterprise" → Key →
// app.redcrypto.de (+ auth.redcrypto.de) zur Domain-Allowlist hinzufügen bzw.
// Origin-Verifikation prüfen. Dann APP_CHECK_ENABLED auf true.
const APP_CHECK_ENABLED = false;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
let _appCheck = null;
// reCAPTCHA App Check funktioniert nicht im nativen Capacitor-WKWebView
// (kein registrierbarer Origin) — nur im Web aktivieren.
// window.Capacitor wird vom Capacitor-Bridge injiziert; im Browser undefined.
const _isCapacitorNative = typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();
if (APP_CHECK_ENABLED && RECAPTCHA_SITE_KEY && !_isCapacitorNative) {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-restricted-globals
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }
  try {
    _appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(RECAPTCHA_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (err) {
    // Im SSR / Test-Kontext oder bei doppelter Init darf das nicht das App-Boot brechen.
    console.warn('[firebase] App Check init übersprungen:', err?.message);
  }
}
export const appCheck = _appCheck;

// Nativ (Capacitor): Auth OHNE Popup/Redirect-Resolver initialisieren.
// getAuth() würde beim ersten Auth-Event einen iframe vom Custom-Auth-Domain laden;
// dessen Cross-Origin-Handshake schlägt in WKWebView mit capacitor://localhost-Origin
// fehl (firebase-js-sdk #5019, #6504).
//
// browserLocalPersistence: Firebase speichert den User in localStorage und pollt
// alle 1 Sekunde auf Änderungen (iOS-Branch in _isMobileBrowser). AuthContext schreibt
// nach dem nativen IDP-Call das User-JSON direkt in localStorage; Firebase erkennt die
// Änderung beim nächsten Poll-Zyklus und feuert onAuthStateChanged.
// Web: getAuth wie bisher (signInWithRedirect braucht den Resolver).
export const auth = _isCapacitorNative
  ? initializeAuth(app, { persistence: browserLocalPersistence })
  : getAuth(app);
export const googleProvider = new GoogleAuthProvider();
export const appleProvider = (() => {
  const p = new OAuthProvider('apple.com');
  p.addScope('email');
  p.addScope('name');
  return p;
})();

// ── Firestore mit persistentLocalCache ────────────────────────────────────────
// IndexedDB-Cache speichert alle TX lokal nach erstem Laden.
// Folgende Sitzungen lesen aus dem Cache → 0 Network-Reads für unveränderte Daten,
// nur Delta-Updates via onSnapshot. Massive Kostenersparnis bei Heavy-Usern.
// persistentMultipleTabManager: synchronisiert den Cache zwischen mehreren Tabs.
// DSGVO: Cache liegt im Browser des Users (IndexedDB), keine externe Datenweitergabe.
let _db;
try {
  // Native iOS (WKWebView): IndexedDB hängt sporadisch auf capacitor://-Origin und
  // WebChannel (Streaming-HTTP) wird von WKWebView gepuffert/geblockt → Firestore
  // onSnapshot feuert nie. Fix: memoryLocalCache (kein IndexedDB) +
  // experimentalAutoDetectLongPolling (versucht WebChannel, fällt auf LongPoll zurück).
  // Web: persistentLocalCache + MultiTab für Tab-Sync.
  const firestoreSettings = _isNative
    // experimentalForceLongPolling ist stabiler als AutoDetect im WKWebView:
    // überspringt den fehlschlagenden WebChannel-Versuch direkt.
    ? { localCache: memoryLocalCache(), experimentalForceLongPolling: true }
    : { localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }) };
  _db = initializeFirestore(app, firestoreSettings);
} catch (err) {
  console.warn('[firebase] Firestore init fallback:', err?.message);
  _db = initializeFirestore(app, {});
}
export const db = _db;

export const storage = getStorage(app);